// apps/web/src/routes/quick-replies.tsx
import { useState } from 'react'
import useSWR from 'swr'
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@viecxanh/chat-ui/table'
import { Button } from '@viecxanh/chat-ui/button'
import { Badge } from '@viecxanh/chat-ui/badge'
import { Input } from '@viecxanh/chat-ui/input'
import { Textarea } from '@viecxanh/chat-ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@viecxanh/chat-ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@viecxanh/chat-ui/dialog'
import { api } from '../lib/api-client'
import type { QuickReply } from '@viecxanh/chat-shared'

const CATEGORIES = ['greeting', 'faq', 'closing', 'support']
const BLANK = { title: '', content: '', category: 'greeting', shortcut: '' }

export function QuickRepliesPage() {
  const { data: replies = [], mutate } = useSWR<QuickReply[]>(
    '/api/admin/quick-replies',
    async (url: string) => {
      const res = await api.get<QuickReply[] | { items: QuickReply[] }>(url)
      return Array.isArray(res) ? res : (res as any)?.items ?? []
    },
  )

  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<QuickReply | null>(null)
  const [form, setForm] = useState(BLANK)
  const [error, setError] = useState('')

  const filtered = replies.filter((r) => {
    const matchCat = !categoryFilter || r.category === categoryFilter
    const matchSearch = !search || r.title.toLowerCase().includes(search.toLowerCase()) || r.shortcut.includes(search)
    return matchCat && matchSearch
  })

  const openCreate = () => { setEditing(null); setForm(BLANK); setError(''); setShowForm(true) }
  const openEdit = (qr: QuickReply) => {
    setEditing(qr)
    setForm({ title: qr.title, content: qr.content, category: qr.category, shortcut: qr.shortcut })
    setError('')
    setShowForm(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!form.shortcut.startsWith('/')) { setError('Shortcut phải bắt đầu bằng "/"'); return }
    try {
      if (editing) {
        await api.put(`/api/admin/quick-replies/${editing._id}`, form)
      } else {
        await api.post('/api/admin/quick-replies', form)
      }
      await mutate()
      setShowForm(false)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Lỗi khi lưu')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Xóa mẫu trả lời này?')) return
    await api.delete(`/api/admin/quick-replies/${id}`)
    await mutate()
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Mẫu trả lời nhanh</h1>
        <Button onClick={openCreate} className="bg-brand hover:bg-brand-dark">+ Thêm mẫu</Button>
      </div>

      <div className="flex gap-3">
        <Input placeholder="Tìm theo tiêu đề hoặc shortcut..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Danh mục" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tiêu đề</TableHead>
            <TableHead>Shortcut</TableHead>
            <TableHead>Danh mục</TableHead>
            <TableHead>Nội dung</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((qr) => (
            <TableRow key={qr._id}>
              <TableCell className="font-medium">{qr.title}</TableCell>
              <TableCell><Badge variant="outline" className="font-mono text-xs">{qr.shortcut}</Badge></TableCell>
              <TableCell>{qr.category}</TableCell>
              <TableCell className="max-w-xs truncate text-slate-500 text-sm">{qr.content}</TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(qr)}>Sửa</Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => void handleDelete(qr._id)}>Xóa</Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Sửa mẫu' : 'Thêm mẫu trả lời'}</DialogTitle></DialogHeader>
          <form onSubmit={(e) => void handleSave(e)} className="space-y-3">
            <Input placeholder="Tiêu đề" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            <Input placeholder="Shortcut (bắt đầu bằng /)" value={form.shortcut} onChange={(e) => setForm({ ...form, shortcut: e.target.value })} required />
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea placeholder="Nội dung mẫu..." value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={4} required />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Hủy</Button>
              <Button type="submit" className="bg-brand hover:bg-brand-dark">Lưu</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
